import styled from '@emotion/styled';
import { Fragment, type ReactNode } from 'react';

import { colors, spacing } from '@/lib/theme';

const Graph = styled.div`
	display: grid;
	grid-template-columns: auto 1fr;
	column-gap: ${spacing.space30};
	row-gap: ${spacing.space20};
	align-items: center;
	flex: 1;
	max-width: 360px;
`;

const Label = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
`;

const Bar = styled.div`
	height: 8px;
	border-radius: 999px;
	background-color: ${colors.LM_NU30};
	overflow: hidden;
`;

const Fill = styled.div<{ fraction: number }>`
	height: 100%;
	width: ${({ fraction }) => `${Math.round(fraction * 100)}%`};
	border-radius: 999px;
	background-color: ${colors.YE100};
`;

export interface FlexGraphItem {
	annotation: ReactNode;
	count: number;
	altText: string;
}

interface FlexGraphProps {
	items: FlexGraphItem[];
}

/** Horizontal bars sized relative to the total: the star-rating breakdown. */
export const FlexGraph = ({ items }: FlexGraphProps) => {
	const total = items.reduce((sum, item) => sum + item.count, 0);
	return (
		<Graph data-testid="flex-graph-component">
			{items.map((item) => (
				<Fragment key={item.altText}>
					<Label aria-hidden>{item.annotation}</Label>
					<Bar
						role="progressbar"
						aria-label={item.altText}
						aria-valuemin={0}
						aria-valuemax={total}
						aria-valuenow={item.count}
						data-testid="bar"
					>
						<Fill fraction={total ? item.count / total : 0} />
					</Bar>
				</Fragment>
			))}
		</Graph>
	);
};
